import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Modal, Inp } from "../pages/staff/Enquiries";
import { Stack, Plus, Check, CircleNotch, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function TreatmentPlansPanel({ patientId, readOnly }) {
  const { user } = useAuth();
  const canDelete = user?.role === "admin";
  const [plans, setPlans] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    const path = readOnly ? "/portal/treatment-plans" : `/treatment-plans?patient_id=${patientId}`;
    const plansRes = await api.get(path);
    setPlans(plansRes.data || []);
    if (!readOnly) {
      const templatesRes = await api.get("/tp-templates");
      setTemplates(templatesRes.data || []);
    }
  };

  useEffect(() => {
    load();
  }, [patientId, readOnly]);

  const toggleItem = async (planId, itemId, currentStatus) => {
    const nextStatus = currentStatus === "completed" ? "pending" : "completed";
    try {
      await api.patch(`/treatment-plans/${planId}/items/${itemId}`, { status: nextStatus });
      toast.success(nextStatus === "completed" ? "Marked complete and logged as treatment" : "Reopened");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removePlan = async (id) => {
    if (!window.confirm("Delete this treatment plan?")) return;
    try {
      await api.delete(`/treatment-plans/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="treatment-plans-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.25em] font-black text-zinc-700">Treatment plans</div>
        {!readOnly ? (
          <button onClick={() => setShowNew(true)} className="text-xs uppercase tracking-[0.2em] font-bold text-[#E60000] inline-flex items-center gap-1" data-testid="add-plan-btn">
            <Plus size={12} weight="bold" /> Apply plan
          </button>
        ) : null}
      </div>

      {plans.length === 0 ? (
        <div className="text-sm text-zinc-500 bg-zinc-50 border border-dashed border-zinc-200 rounded-sm p-6 text-center">
          {readOnly ? "No treatment plan assigned yet." : "No active plans. Apply a template or create a custom plan."}
        </div>
      ) : (
        <div className="space-y-3">
          {plans.map((plan) => {
            const total = (plan.items || []).reduce((sum, item) => sum + (item.cost || 0), 0);
            const doneCount = (plan.items || []).filter((item) => item.status === "completed").length;
            const percent = plan.items?.length ? Math.round((doneCount / plan.items.length) * 100) : 0;
            return (
              <div key={plan.id} className="bg-white border border-zinc-200 rounded-sm p-4" data-testid={`plan-${plan.id}`}>
                <div className="flex items-start gap-2">
                  <Stack size={18} weight="duotone" color="#E60000" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="font-bold text-zinc-900">{plan.name}</div>
                      <span className={`text-[10px] uppercase tracking-[0.2em] font-bold ${plan.status === "completed" ? "text-green-700" : "text-blue-700"}`}>
                        {String(plan.status || "pending").replace("_", " ")}
                      </span>
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      {doneCount} / {plan.items?.length || 0} visits · Rs {total.toFixed(0)} total
                    </div>
                    <div className="h-1 bg-zinc-100 rounded-sm mt-2 overflow-hidden">
                      <div className="h-1 bg-[#E60000]" style={{ width: `${percent}%` }} />
                    </div>
                  </div>
                  {!readOnly && canDelete ? (
                    <button onClick={() => removePlan(plan.id)} className="text-zinc-400 hover:text-[#E60000]" data-testid={`plan-delete-${plan.id}`}>
                      <Trash size={14} weight="bold" />
                    </button>
                  ) : null}
                </div>
                <div className="mt-3 space-y-1">
                  {(plan.items || []).map((item) => {
                    const done = item.status === "completed";
                    return (
                      <div key={item.id} className="flex items-center gap-2 text-sm">
                        {readOnly ? (
                          done ? <Check size={14} weight="bold" color="#16A34A" /> : <CircleNotch size={14} weight="bold" color="#A1A1AA" />
                        ) : (
                          <button
                            onClick={() => toggleItem(plan.id, item.id, item.status)}
                            className={`w-5 h-5 border rounded-sm flex items-center justify-center ${done ? "bg-green-600 border-green-600" : "border-zinc-300 hover:border-zinc-900"}`}
                            data-testid={`plan-item-${item.id}`}
                          >
                            {done ? <Check size={12} weight="bold" color="white" /> : null}
                          </button>
                        )}
                        <span className={`flex-1 ${done ? "line-through text-zinc-400" : "text-zinc-800"}`}>{item.title}</span>
                        {item.cost ? <span className="text-xs text-zinc-500">Rs {item.cost}</span> : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!readOnly && showNew ? (
        <ApplyPlanModal
          patientId={patientId}
          templates={templates}
          onClose={() => setShowNew(false)}
          onSaved={() => {
            setShowNew(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}

function ApplyPlanModal({ patientId, templates, onClose, onSaved }) {
  const [mode, setMode] = useState("template");
  const [templateId, setTemplateId] = useState(templates[0]?.id || "");
  const [name, setName] = useState("");
  const [items, setItems] = useState([{ title: "", default_cost: 0 }]);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { patient_id: patientId };
      if (mode === "template") {
        if (!templateId) {
          toast.error("Pick a template");
          setBusy(false);
          return;
        }
        payload.template_id = templateId;
        if (name) payload.name = name;
      } else {
        payload.name = name || "Custom plan";
        payload.items = items
          .filter((item) => item.title)
          .map((item, index) => ({
            title: item.title,
            default_cost: Number(item.default_cost) || 0,
            sequence: index,
          }));
        if (payload.items.length === 0) {
          toast.error("Add at least one item");
          setBusy(false);
          return;
        }
      }
      await api.post("/treatment-plans", payload);
      toast.success("Plan applied");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Apply treatment plan">
      <div className="inline-flex border border-zinc-300 rounded-sm mb-4">
        <button onClick={() => setMode("template")} className={`px-3 py-1.5 text-xs uppercase tracking-wider font-bold ${mode === "template" ? "bg-zinc-900 text-white" : "text-zinc-700"}`} data-testid="plan-mode-template">
          From template
        </button>
        <button onClick={() => setMode("custom")} className={`px-3 py-1.5 text-xs uppercase tracking-wider font-bold ${mode === "custom" ? "bg-zinc-900 text-white" : "text-zinc-700"}`} data-testid="plan-mode-custom">
          Custom
        </button>
      </div>

      {mode === "template" ? (
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700">Template</label>
            <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} className="mt-2 w-full border border-zinc-300 rounded-sm px-3 py-2 bg-white focus:outline-none focus:border-zinc-900" data-testid="plan-template-select">
              <option value="">- pick -</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name} ({template.items?.length || 0} visits)
                </option>
              ))}
            </select>
          </div>
          <Inp label="Override plan name (optional)" v={name} onChange={setName} testid="plan-name" />
        </div>
      ) : (
        <div className="space-y-3">
          <Inp label="Plan name" v={name} onChange={setName} testid="plan-name" />
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700 mb-2">Items</div>
            {items.map((item, index) => (
              <div key={index} className="grid grid-cols-12 gap-2 mb-2">
                <input
                  value={item.title}
                  onChange={(e) => setItems((state) => state.map((entry, idx) => (idx === index ? { ...entry, title: e.target.value } : entry)))}
                  className="col-span-8 border border-zinc-300 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-zinc-900"
                  placeholder="Visit title"
                  data-testid={`plan-custom-title-${index}`}
                />
                <input
                  type="number"
                  value={item.default_cost}
                  onChange={(e) => setItems((state) => state.map((entry, idx) => (idx === index ? { ...entry, default_cost: e.target.value } : entry)))}
                  className="col-span-3 border border-zinc-300 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-zinc-900"
                  placeholder="Rs"
                  data-testid={`plan-custom-cost-${index}`}
                />
                <button onClick={() => setItems((state) => state.filter((_, idx) => idx !== index))} className="col-span-1 text-zinc-400 hover:text-[#E60000]">
                  <Trash size={14} />
                </button>
              </div>
            ))}
            <button onClick={() => setItems([...items, { title: "", default_cost: 0 }])} className="text-xs uppercase tracking-[0.2em] font-bold text-[#E60000]" data-testid="plan-custom-add">
              + Add
            </button>
          </div>
        </div>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="bg-transparent border border-zinc-900 text-zinc-900 font-semibold rounded-sm px-4 py-2 text-sm">
          Cancel
        </button>
        <button onClick={save} disabled={busy} className="bg-[#E60000] hover:bg-[#CC0000] text-white font-semibold rounded-sm px-4 py-2 text-sm disabled:opacity-60" data-testid="plan-apply-save">
          {busy ? "Applying..." : "Apply plan"}
        </button>
      </div>
    </Modal>
  );
}
